import { Button, DialogContent, DialogTitle } from '@atlas/ui-primitives'
import { useId } from 'react'

export interface ConfirmDialogProps {
	open: boolean
	title: string
	description?: string
	confirmLabel?: string
	cancelLabel?: string
	submitting?: boolean
	/**
	 * The confirm button's style. `danger` for an action that destroys something, so the
	 * control looks like what it does — the copy alone is easy to click past.
	 */
	confirmVariant?: 'primary' | 'danger'
	/**
	 * What the confirm button says while the action runs. Defaults to "Saving…", which is
	 * wrong for anything that isn't a save — a delete says "Deleting…".
	 */
	busyLabel?: string
	/**
	 * Safe, pre-formatted error message; the dialog stays open so the user can retry
	 * with the reason next to the control. Shown IN ADDITION to the status-bar report,
	 * which is the surface that always gets the message.
	 */
	errorMessage?: string | null
	/**
	 * Refuses the action for a reason of the caller's own — a precondition the dialog
	 * itself surfaces, such as "this list is still referenced". Distinct from
	 * `submitting`, which is the action already running: this one leaves Cancel live,
	 * because there is nothing in flight to wait for.
	 */
	confirmDisabled?: boolean
	onConfirm: () => void
	onCancel: () => void
	/** Optional extra content (e.g. a role picker) rendered above the actions. */
	children?: React.ReactNode
}

/**
 * Generic confirm-before-acting modal (design language of `CreateWorkOrderDialog`).
 * The one confirmation surface for destructive actions app-wide — replaces native
 * `window.confirm` calls: the caller supplies the copy, the style and the action.
 * Contains keyboard focus while open and cancels on Escape. Presentational + storied.
 */
export function ConfirmDialog({
	open,
	title,
	description,
	confirmLabel = 'Confirm',
	cancelLabel = 'Cancel',
	submitting,
	confirmVariant = 'primary',
	busyLabel = 'Saving…',
	errorMessage,
	confirmDisabled,
	onConfirm,
	onCancel,
	children,
}: ConfirmDialogProps) {
	const descriptionId = useId()

	// While the action runs it can no longer be backed out of — Escape and Cancel
	// go quiet rather than closing a dialog whose mutation completes anyway.
	return (
		<DialogContent
			size="sm"
			isOpen={open}
			onOpenChange={(next) => {
				if (!next) onCancel()
			}}
			isKeyboardDismissDisabled={submitting}
			aria-describedby={description ? descriptionId : undefined}
		>
			<DialogTitle>{title}</DialogTitle>
			{description ? (
				<p id={descriptionId} className="mt-2 text-ds-sm leading-5 text-ds-text-muted">
					{description}
				</p>
			) : null}
			{children ? <div className="mt-4">{children}</div> : null}
			{errorMessage ? (
				<p role="alert" className="mt-2 text-ds-sm text-ds-danger">
					{errorMessage}
				</p>
			) : null}
			<div className="mt-6 flex justify-end gap-2">
				<Button
					type="button"
					variant="secondary"
					className="rounded-ds-full"
					isDisabled={submitting}
					onPress={onCancel}
				>
					{cancelLabel}
				</Button>
				<Button
					type="button"
					variant={confirmVariant}
					isDisabled={submitting || confirmDisabled}
					className="rounded-ds-full px-5"
					onPress={onConfirm}
				>
					{submitting ? busyLabel : confirmLabel}
				</Button>
			</div>
		</DialogContent>
	)
}
